import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Station } from "@shared/models";
import { useLoader } from "@shared/hooks";
import {
  StationViewRawDataChart,
  StationViewRawDataHeader,
} from "@containers/Station/components/StationViewRawDataContainer";
import { MONGO_INTERVAL_OPTIONS } from "@shared/constants";
import { useDispatch, useSelector } from "react-redux";
import { selectors, actions } from "@containers/Station/store";
import { StationViewRawDataFilter } from "@shared/interfaces";
import { addDaysToDate, addSecondsToDate, formatDateToTz, getCurrentTimezone } from "@shared/utils";
import { StationViewRawDataDelayLoader } from "@containers/Station/components/StationViewRawDataContainer/StationViewRawDataDelayLoader";

import "./index.scss";

interface StationViewRawDataContainerProps {
  station: Station;
  onClose: () => void;
}

const StationViewRawDataContainer: FC<StationViewRawDataContainerProps> = ({ onClose, station }) => {
  const dispatch = useDispatch();

  const [isDelay, setIsDelay] = useState(true);
  const filter = useSelector(selectors.getStationRawDataFilter());
  const rawData = useSelector(selectors.getStationRawData());

  const stationStartDate = useMemo(() => new Date(), []);
  const stationEndDate = useMemo(() => addDaysToDate(new Date(), 1), []);

  const { isLoading } = useLoader({
    name: "getStationRawData",
    actionTypes: useMemo(() => [actions.getStationRawData], []),
  });

  const { isLoading: isDeactivateStationLoading } = useLoader({
    name: "activateDeactivateStation",
    actionTypes: useMemo(() => [actions.activateDeactivateStation], []),
  });

  const handleClose = useCallback(() => {
    dispatch(
      actions.activateDeactivateStation.request({
        id: Number(station.id),
        activate: false,
        cb: () => onClose(),
        cbError: () => onClose(),
      }),
    );
  }, [dispatch, onClose, station.id]);

  const changeFilter = useCallback(
    (updatedFilter: Partial<StationViewRawDataFilter>) => {
      dispatch(
        actions.setStationViewRawDataFilter({
          ...((filter || {}) as StationViewRawDataFilter),
          ...updatedFilter,
        }),
      );
    },
    [dispatch, filter],
  );

  useEffect(() => {
    if (!filter && !isDelay) {
      const [, interval] = MONGO_INTERVAL_OPTIONS;

      changeFilter({
        interval,
        start: stationStartDate.toISOString(),
        end: addSecondsToDate(stationStartDate, interval.value).toISOString(),
      });
    }
  }, [filter, changeFilter, isDelay, stationStartDate]);

  useEffect(() => {
    if (filter) {
      dispatch(
        actions.getStationRawData.request({
          station_id: station.id,
          filter: {
            start: formatDateToTz(filter.start, getCurrentTimezone()),
            end: formatDateToTz(filter.end, getCurrentTimezone()),
          },
        }),
      );
    }
  }, [dispatch, station.id, filter, station.timezone]);

  useEffect(() => {
    return () => {
      dispatch(actions.setStationViewRawDataFilter(null));
      dispatch(actions.getStationRawData.success([]));
    };
  }, [dispatch]);

  return (
    <div className="station-view-raw-data-container">
      <StationViewRawDataHeader
        onClose={handleClose}
        isCloseDisabled={isDelay}
        isCloseLoading={isDeactivateStationLoading}
      />
      <div className="station-view-raw-data-chart">
        <StationViewRawDataDelayLoader onComplete={() => setIsDelay(false)} isLoading={isDelay} />
        <StationViewRawDataChart
          isLoading={isLoading}
          filter={filter}
          onChangeFilter={changeFilter}
          rows={rawData}
          station={station}
          startDate={stationStartDate}
          endDate={stationEndDate}
        />
      </div>
    </div>
  );
};

export default StationViewRawDataContainer;
